import React, { useState, useEffect } from 'react';
import { Desktop, Plus, Trash, X, Copy } from '@phosphor-icons/react';
import api from '../../utils/api';
import { toast } from 'sonner';

export const KioskAccountsSection = ({ shows }) => {
  const [accounts, setAccounts] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [username, setUsername] = useState('');
  const [pin, setPin] = useState('');
  const [selectedShowId, setSelectedShowId] = useState('');
  const [selectedRole, setSelectedRole] = useState('kiosk');
  const [creating, setCreating] = useState(false);

  useEffect(() => { loadAccounts(); }, []);

  const loadAccounts = async () => {
    try {
      const { data } = await api.get('/kiosk/accounts');
      setAccounts(data);
    } catch { setAccounts([]); }
  };

  const handleCreate = async () => {
    if (!username.trim() || !pin.trim() || !selectedShowId) {
      toast.error('Fill in all fields'); return;
    }
    setCreating(true);
    try {
      const { data } = await api.post('/kiosk/accounts', {
        username: username.trim(), pin: pin.trim(), show_id: selectedShowId, role: selectedRole
      });
      toast.success(data.message);
      setShowCreate(false);
      setUsername(''); setPin(''); setSelectedShowId(''); setSelectedRole('kiosk');
      loadAccounts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
    finally { setCreating(false); }
  };

  const handleDelete = async (id, name) => {
    try {
      await api.delete(`/kiosk/accounts/${id}`);
      toast.success(`Kiosk '${name}' deleted`);
      loadAccounts();
    } catch { toast.error('Failed to delete'); }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/kiosk`;
    navigator.clipboard.writeText(url);
    toast.success('Kiosk link copied!');
  };

  // Active shows = anything in DRAFT/PUBLISHED/LIVE state (not COMPLETED — kiosks aren't useful then).
  const activeShows = (shows || []).filter(s => ['DRAFT', 'PUBLISHED', 'LIVE'].includes(s.state));
  const noShowsAvailable = activeShows.length === 0;

  return (
    <div className="mb-8 bg-[#161616] border border-[#262626] rounded-lg p-5" data-testid="kiosk-accounts-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Desktop size={18} className="text-[#00D4FF]" />
          <h3 className="text-lg font-bold text-white" style={{fontFamily: 'Outfit'}}>Kiosk Accounts</h3>
          <span className="text-[#525252] text-xs">({accounts.length})</span>
        </div>
        <div className="flex gap-2">
          <button onClick={copyLink} className="flex items-center gap-1 px-2 py-1 text-[#9E9E9E] hover:text-white text-xs" title="Copy kiosk URL">
            <Copy size={12} /> Link
          </button>
          {!showCreate && (
            <button
              onClick={() => setShowCreate(true)}
              disabled={noShowsAvailable}
              title={noShowsAvailable ? 'Create or publish a show first to attach kiosks to' : 'New kiosk account'}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#00D4FF]/15 text-[#00D4FF] border border-[#00D4FF]/30 rounded-md text-sm font-semibold hover:bg-[#00D4FF]/25 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="create-kiosk-btn"
            >
              <Plus size={14} weight="bold" /> New Kiosk
            </button>
          )}
        </div>
      </div>

      <p className="text-[#9E9E9E] text-xs mb-4">Kiosk accounts let people enter at the show without an email or login. Entries are marked UNPAID until collected at the office.</p>

      {noShowsAvailable && (
        <div className="bg-[#FF9500]/10 border border-[#FF9500]/30 rounded-lg p-3 mb-4 text-[#FF9500] text-xs" data-testid="kiosk-no-shows-warning">
          You don&apos;t have any DRAFT/PUBLISHED/LIVE shows yet. Create a show first to attach a kiosk to it.
        </div>
      )}

      {showCreate && (
        <div className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-4 mb-4" data-testid="create-kiosk-form">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-3">
            <div>
              <label className="block text-xs text-[#9E9E9E] mb-1">Username</label>
              <input type="text" value={username} onChange={(e) => setUsername(e.target.value)}
                placeholder="e.g. gate1" className="w-full px-3 py-2 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#00D4FF]"
                data-testid="kiosk-new-username" />
            </div>
            <div>
              <label className="block text-xs text-[#9E9E9E] mb-1">PIN (4+ digits)</label>
              <input type="text" value={pin} onChange={(e) => setPin(e.target.value)}
                placeholder="1234" inputMode="numeric" className="w-full px-3 py-2 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#00D4FF]"
                data-testid="kiosk-new-pin" />
            </div>
            <div>
              <label className="block text-xs text-[#9E9E9E] mb-1">Role</label>
              <select value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)}
                className="w-full px-3 py-2 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#00D4FF]"
                data-testid="kiosk-new-role">
                <option value="kiosk">Kiosk Entry</option>
                <option value="timekeeper">Timekeeper</option>
                <option value="office">Office Staff</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-[#9E9E9E] mb-1">Show</label>
              <select value={selectedShowId} onChange={(e) => setSelectedShowId(e.target.value)}
                className="w-full px-3 py-2 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#00D4FF]"
                data-testid="kiosk-new-show">
                <option value="">Select show...</option>
                {activeShows.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleCreate} disabled={creating} className="px-4 py-2 bg-[#00D4FF] text-white text-sm font-bold rounded-md hover:bg-[#00D4FF]/80 disabled:opacity-50" data-testid="kiosk-create-confirm">
              {creating ? '...' : 'Create'}
            </button>
            <button onClick={() => { setShowCreate(false); setUsername(''); setPin(''); setSelectedShowId(''); }} className="p-2 text-[#9E9E9E] hover:text-white">
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {accounts.length === 0 ? (
        <p className="text-[#525252] text-sm">No kiosk accounts yet.</p>
      ) : (
        <div className="space-y-2">
          {accounts.map(a => (
            <div key={a.id} className="flex items-center justify-between bg-[#0A0A0A] border border-[#262626] rounded-lg px-4 py-3" data-testid={`kiosk-account-${a.id}`}>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-white text-sm font-semibold">{a.display_name}</p>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                    a.role === 'timekeeper' ? 'bg-[#34C759]/15 text-[#34C759]' :
                    a.role === 'office' ? 'bg-[#0096FF]/15 text-[#0096FF]' :
                    'bg-[#FF9500]/15 text-[#FF9500]'
                  }`}>{a.role === 'timekeeper' ? 'Timekeeper' : a.role === 'office' ? 'Office' : 'Kiosk'}</span>
                </div>
                <p className="text-[#9E9E9E] text-xs">{a.show_name}</p>
              </div>
              <button onClick={() => handleDelete(a.id, a.display_name)} className="text-[#FF3B30] hover:text-[#FF3B30]/80" data-testid={`kiosk-delete-${a.id}`}>
                <Trash size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
